import { useState } from 'react';
import { ArrowLeft, Globe, FileText, Target } from 'lucide-react';
import { getLogoUrl } from '../../lib/logo';
import type { VendorIntelligence, VendorCompetitor, VerticalDifferentiator, VendorBattlecard } from '../../types';
import CompetitorDetail from './CompetitorDetail';

interface Props {
  competitors: VendorCompetitor[];
  differentiators: VerticalDifferentiator[];
  battlecards: VendorBattlecard[];
  vendorIntelligence: VendorIntelligence;
}

export default function CompetitorsTab({ competitors, differentiators, battlecards, vendorIntelligence }: Props) {
  const [selectedCompetitor, setSelectedCompetitor] = useState<VendorCompetitor | null>(null);

  if (selectedCompetitor) {
    const compDiffs = differentiators.filter(d => d.competitor_id === selectedCompetitor.id);
    const battlecard = battlecards.find(b => b.competitor_id === selectedCompetitor.id) || null;
    return (
      <div>
        <button
          onClick={() => setSelectedCompetitor(null)}
          className="flex items-center gap-2 text-sm text-app-muted hover:text-app-ink mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Competitors
        </button>
        <CompetitorDetail
          competitor={selectedCompetitor}
          differentiators={compDiffs}
          battlecard={battlecard}
          vendorIntelligence={vendorIntelligence}
        />
      </div>
    );
  }

  return (
    <div>
      {competitors.length === 0 ? (
        <div className="text-center py-20">
          <Target className="w-12 h-12 text-app-line-strong mx-auto mb-3" />
          <h3 className="text-base font-medium text-app-muted mb-1">No Competitor Data Available</h3>
          <p className="text-sm text-app-muted">Competitor intelligence will appear here once your admin has completed the research.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {competitors.map(comp => {
            const compDiffs = differentiators.filter(d => d.competitor_id === comp.id);
            const verticalCount = new Set(compDiffs.map(d => d.vertical_name)).size;
            const hasBattlecard = battlecards.some(b => b.competitor_id === comp.id);
            const strengths = compDiffs.filter(d => d.category === 'strength').length;
            const weaknesses = compDiffs.filter(d => d.category === 'weakness').length;

            return (
              <div
                key={comp.id}
                onClick={() => setSelectedCompetitor(comp)}
                className="bg-app-surface rounded-lg border border-app-line shadow-none p-5 cursor-pointer hover:shadow-none hover:border-forest-200 transition group"
              >
                <div className="flex items-start gap-4">
                  <img
                    src={getLogoUrl(comp.competitor_url)}
                    alt=""
                    className="w-14 h-14 rounded-lg object-contain bg-app-canvas p-1.5 flex-shrink-0 group-hover:scale-105 transition-transform"
                    onError={e => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comp.competitor_name)}&background=e5e7eb&color=374151&size=56`; }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-semibold text-app-ink truncate">{comp.competitor_name}</h4>
                    {comp.competitor_url && (
                      <p className="text-xs text-app-muted truncate mt-0.5">
                        <Globe className="w-3 h-3 inline mr-1" />
                        {comp.competitor_url.replace(/^https?:\/\/(www\.)?/, '')}
                      </p>
                    )}
                    {comp.competitor_docs_url && (
                      <p className="text-xs text-app-muted truncate mt-0.5">
                        <FileText className="w-3 h-3 inline mr-1" />
                        Docs available
                      </p>
                    )}
                  </div>
                </div>
                {comp.executive_overview && (
                  <p className="text-xs text-app-muted mt-3 line-clamp-3 leading-relaxed">
                    {comp.executive_overview.substring(0, 200)}...
                  </p>
                )}
                <div className="mt-4 pt-3 border-t border-app-line flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    {hasBattlecard && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-forest-50 text-forest-700">
                        Battlecard
                      </span>
                    )}
                    {strengths > 0 && (
                      <span className="text-xs text-app-positive">
                        {strengths} strength{strengths > 1 ? 's' : ''}
                      </span>
                    )}
                    {weaknesses > 0 && (
                      <span className="text-xs text-app-warning">
                        {weaknesses} weakness{weaknesses > 1 ? 'es' : ''}
                      </span>
                    )}
                    {verticalCount > 0 && (
                      <span className="text-xs text-app-muted">
                        {verticalCount} vertical{verticalCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-forest-600 font-medium group-hover:text-forest-800 transition-colors">
                    View details
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
